import { useOrganizationContext } from "@src/modules/member/context/organization-context/hook";
import {
  AddOn,
  AddOnActivationPreview,
  BillingInvoice,
  DowngradePreview,
  Plan,
  Subscription,
  SubscriptionChange,
  UpgradePreview,
  UsageMap,
} from "@src/modules/member/models/subscription";
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BackendResponseFormat,
  MutationDefaultOptions,
  backendFetch,
} from "@utils";

import { useLanguage } from "../../../hooks/helperHooks";
import { BASE_BACKEND_MEMBER_PATH } from "../constants";

export const queryKeys = {
  subscription: "SUBSCRIPTION",
  usage: "SUBSCRIPTION_USAGE",
  plans: "SUBSCRIPTION_PLANS",
  addOns: "SUBSCRIPTION_ADD_ONS",
  billingHistory: "SUBSCRIPTION_BILLING_HISTORY",
};

const base = () =>
  `${import.meta.env.VITE_BACKEND_BASE_PATH}${BASE_BACKEND_MEMBER_PATH}`;

type UseSubscriptionResult = UseQueryResult<
  BackendResponseFormat<Subscription>
>;
export function useSubscription(): UseSubscriptionResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useQuery({
    enabled: !!currentOrganizationCode,
    queryKey: [queryKeys.subscription, currentOrganizationCode],
    queryFn: async () => {
      const response = await backendFetch({
        method: "GET",
        url: `${base()}/subscription`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<Subscription>;
    },
  });
}

type UseUsageResult = UseQueryResult<BackendResponseFormat<UsageMap>>;
export function useUsage(): UseUsageResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useQuery({
    enabled: !!currentOrganizationCode,
    queryKey: [queryKeys.usage, currentOrganizationCode],
    queryFn: async () => {
      const response = await backendFetch({
        method: "GET",
        url: `${base()}/subscription/usage`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<UsageMap>;
    },
  });
}

type UsePlansResult = UseQueryResult<BackendResponseFormat<Plan[]>>;
export function usePlans(): UsePlansResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useQuery({
    enabled: !!currentOrganizationCode,
    queryKey: [queryKeys.plans, currentOrganizationCode],
    queryFn: async () => {
      const response = await backendFetch({
        method: "GET",
        url: `${base()}/subscription/plans`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<Plan[]>;
    },
  });
}

type UseAddOnsResult = UseQueryResult<BackendResponseFormat<AddOn[]>>;
export function useAddOns(): UseAddOnsResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useQuery({
    enabled: !!currentOrganizationCode,
    queryKey: [queryKeys.addOns, currentOrganizationCode],
    queryFn: async () => {
      const response = await backendFetch({
        method: "GET",
        url: `${base()}/subscription/add-ons`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<AddOn[]>;
    },
  });
}

/**
 * Convenience: is the named add-on currently active for this org?
 *
 * Returns `{ active, isLoading }`. While the underlying add-on list is loading,
 * `active` is `false` so callers can render skeletons via `isLoading` rather
 * than mistaking "loading" for "not active".
 */
export function useOrganizationHasAddOn(slug: string): {
  active: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useAddOns();
  const addOns = data?.data ?? [];
  const active = addOns.some(
    (addOn) => addOn.slug === slug && addOn.is_activated_for_org,
  );

  return { active, isLoading };
}

type UseBillingHistoryResult = UseQueryResult<
  BackendResponseFormat<BillingInvoice[]>
>;
export function useBillingHistory({
  page = 1,
  pageSize = 25,
  searchParams = null,
}: {
  page?: number;
  pageSize?: number;
  searchParams?: URLSearchParams | null;
} = {}): UseBillingHistoryResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useQuery({
    enabled: !!currentOrganizationCode,
    queryKey: [
      queryKeys.billingHistory,
      currentOrganizationCode,
      page,
      searchParams?.toString(),
    ],
    queryFn: async () => {
      let url = `${base()}/subscription/billing-history?page=${page}&per_page=${pageSize}`;
      if (searchParams?.toString()) {
        url += `&${searchParams.toString()}`;
      }

      const response = await backendFetch({
        method: "GET",
        url,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<BillingInvoice[]>;
    },
  });
}

type UseUpgradePreviewResult = UseQueryResult<
  BackendResponseFormat<UpgradePreview>
>;
export function useUpgradePreview(enabled = true): UseUpgradePreviewResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useQuery({
    enabled: enabled && !!currentOrganizationCode,
    queryKey: [
      queryKeys.subscription,
      currentOrganizationCode,
      "upgrade-preview",
    ],
    // The endpoint 409s once the org is already Pro. That's expected when the
    // upgrade mutation succeeds and invalidates subscription queries — we don't
    // want that transient 409 to bubble to the global error boundary.
    throwOnError: false,
    queryFn: async () => {
      const response = await backendFetch({
        method: "GET",
        url: `${base()}/subscription/upgrade-preview`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<UpgradePreview>;
    },
  });
}

type UseDowngradePreviewResult = UseQueryResult<
  BackendResponseFormat<DowngradePreview>
>;
export function useDowngradePreview(enabled = true): UseDowngradePreviewResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useQuery({
    enabled: enabled && !!currentOrganizationCode,
    queryKey: [
      queryKeys.subscription,
      currentOrganizationCode,
      "downgrade-preview",
    ],
    queryFn: async () => {
      const response = await backendFetch({
        method: "GET",
        url: `${base()}/subscription/downgrade-preview`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<DowngradePreview>;
    },
  });
}

export interface UpgradeSubscriptionRequest {
  payment_method_id?: string | null;
}

type UseUpgradeResult = UseMutationResult<
  BackendResponseFormat<Subscription>,
  Error,
  UpgradeSubscriptionRequest,
  unknown
>;
export function useUpgrade(
  mutationOptions: MutationDefaultOptions,
): UseUpgradeResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await backendFetch({
        method: "POST",
        url: `${base()}/subscription/upgrade`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
        body: data,
      });

      return response as BackendResponseFormat<Subscription>;
    },
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.subscription, currentOrganizationCode],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.usage, currentOrganizationCode],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.billingHistory, currentOrganizationCode],
      });
    },
  });
}

type UseDowngradeResult = UseMutationResult<
  BackendResponseFormat<SubscriptionChange>,
  Error,
  void,
  unknown
>;
export function useDowngrade(
  mutationOptions: MutationDefaultOptions,
): UseDowngradeResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await backendFetch({
        method: "POST",
        url: `${base()}/subscription/downgrade`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<SubscriptionChange>;
    },
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.subscription, currentOrganizationCode],
      });
    },
  });
}

type UseCancelDowngradeResult = UseMutationResult<
  BackendResponseFormat<SubscriptionChange>,
  Error,
  void,
  unknown
>;
export function useCancelDowngrade(
  mutationOptions: MutationDefaultOptions,
): UseCancelDowngradeResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await backendFetch({
        method: "DELETE",
        url: `${base()}/subscription/downgrade`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<SubscriptionChange>;
    },
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.subscription, currentOrganizationCode],
      });
    },
  });
}

type UseAddOnPreviewResult = UseQueryResult<
  BackendResponseFormat<AddOnActivationPreview>
>;
export function useAddOnActivationPreview(
  code: string | undefined,
  enabled = true,
): UseAddOnPreviewResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();

  return useQuery({
    enabled: enabled && !!code && !!currentOrganizationCode,
    queryKey: [queryKeys.addOns, currentOrganizationCode, "preview", code],
    throwOnError: false,
    queryFn: async () => {
      const response = await backendFetch({
        method: "GET",
        url: `${base()}/subscription/add-ons/${code}/preview`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<AddOnActivationPreview>;
    },
  });
}

type UseActivateAddOnResult = UseMutationResult<
  BackendResponseFormat<AddOn>,
  Error,
  string,
  unknown
>;
export function useActivateAddOn(
  mutationOptions: MutationDefaultOptions,
): UseActivateAddOnResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      const response = await backendFetch({
        method: "POST",
        url: `${base()}/subscription/add-ons/${code}/activate`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<AddOn>;
    },
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.addOns, currentOrganizationCode],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.subscription, currentOrganizationCode],
      });
    },
  });
}

type UseDeactivateAddOnResult = UseMutationResult<
  BackendResponseFormat<AddOn>,
  Error,
  string,
  unknown
>;
export function useDeactivateAddOn(
  mutationOptions: MutationDefaultOptions,
): UseDeactivateAddOnResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      const response = await backendFetch({
        method: "POST",
        url: `${base()}/subscription/add-ons/${code}/deactivate`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
      });

      return response as BackendResponseFormat<AddOn>;
    },
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.addOns, currentOrganizationCode],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.subscription, currentOrganizationCode],
      });
    },
  });
}

export interface UpdateOverageSettingsRequest {
  overage_enabled: boolean;
  overage_spending_cap?: number | null;
}

type UseUpdateOverageSettingsResult = UseMutationResult<
  BackendResponseFormat<Subscription>,
  Error,
  UpdateOverageSettingsRequest,
  unknown
>;
export function useUpdateOverageSettings(
  mutationOptions: MutationDefaultOptions,
): UseUpdateOverageSettingsResult {
  const language = useLanguage();
  const { currentOrganizationCode } = useOrganizationContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await backendFetch({
        method: "PATCH",
        url: `${base()}/subscription/overage`,
        language,
        headers: {
          "Organization-Code": currentOrganizationCode as string,
        },
        body: data,
      });

      return response as BackendResponseFormat<Subscription>;
    },
    meta: {
      errorKey: mutationOptions.errorKey,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.subscription, currentOrganizationCode],
      });
    },
  });
}
